/**
 * KB CARD — scheletro CONDIVISO della card di bacheca (preventivi e task).
 *
 * Un'unica implementazione di: root `.kb-card` (+ eventuale modificatore, es.
 * `is-done`), riga `__top` (slot iniziale opzionale + titolo + cestino), riga
 * `__meta` e riga `__foot`. Le card delle entità (`QuotationKanbanCard`,
 * `TaskKanbanCard`) NON ridisegnano questo markup: passano solo i propri dati e
 * gli slot entity-specific (meta/foot). Così cestino, titolo e disposizione sono
 * identici ovunque — niente divergenze (una sola icona, un solo stile).
 *
 * Titolo polimorfico ma unico: `titleTo` → `<Link>` (naviga), altrimenti
 * `onTitleClick` → `<button>` (es. apre un drawer). `draggable` centralizza lo
 * stop della propagazione del pointer su TUTTE le parti cliccabili — slot
 * iniziale, titolo (entrambi i rami) e cestino — così cliccarle non avvia il
 * trascinamento della card (board interattiva).
 *
 * Il pointer e non il click: dnd-kit fa partire il trascinamento su `pointerdown`,
 * e un `onClick` che ferma la propagazione arriva troppo tardi — il drag è già
 * cominciato e il click non si vedrà mai. È il motivo per cui lo `stopPropagation`
 * che `TaskCompletionToggle` ha già al suo interno non basta qui.
 */

import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

/**
 * Cestino: o entrambe le prop o nessuna. Coppia e non due opzionali indipendenti perché
 * `deleteLabel` è l'`aria-label` del bottone: un cestino senza label sarebbe un controllo
 * muto per uno screen reader, e nulla lo segnalerebbe a compile time.
 */
type KbCardDelete =
  | { onDelete: () => void; deleteLabel: string }
  | { onDelete?: undefined; deleteLabel?: undefined };

type KbCardProps = {
  /** Classi extra sul root, es. `is-done`. */
  modifier?: string;
  /** Slot iniziale della riga top (es. la spunta del task). */
  lead?: ReactNode;
  /** Testo/label del titolo. */
  title: ReactNode;
  /** Se presente, il titolo è un `<Link>` verso questo path. */
  titleTo?: string;
  /** Altrimenti il titolo è un `<button>` con questo handler (es. apre drawer). */
  onTitleClick?: () => void;
  /** Card trascinabile: ferma il pointer sulle parti cliccabili (no drag sul click). */
  draggable?: boolean;
  meta?: ReactNode;
  foot?: ReactNode;
} & KbCardDelete;

export function KbCard({
  modifier,
  lead,
  title,
  titleTo,
  onTitleClick,
  onDelete,
  deleteLabel,
  draggable = false,
  meta,
  foot,
}: KbCardProps) {
  const stopDrag = draggable ? (e: ReactPointerEvent) => e.stopPropagation() : undefined;

  return (
    <div className={`kb-card${modifier ? ` ${modifier}` : ''}`}>
      <div className="kb-card__top">
        {/* `display: contents` in SCSS: lo span esiste per intercettare il pointer, non
            per entrare nel flex del `__top`. Lo slot resta disegnato dov'era. */}
        {lead && (
          <span className="kb-card__lead" onPointerDown={stopDrag}>
            {lead}
          </span>
        )}
        {titleTo ? (
          <Link to={titleTo} className="kb-card__title" onPointerDown={stopDrag}>
            {title}
          </Link>
        ) : (
          <button
            type="button"
            className="kb-card__title"
            onPointerDown={stopDrag}
            onClick={onTitleClick}
          >
            {title}
          </button>
        )}
        {onDelete && (
          <IconButton
            size="small"
            className="kb-card__del"
            aria-label={deleteLabel}
            onPointerDown={stopDrag}
            onClick={onDelete}
          >
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {meta && <div className="kb-card__meta">{meta}</div>}
      {foot && <div className="kb-card__foot">{foot}</div>}
    </div>
  );
}
